// Libs
import classNames from 'classnames/bind';
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { Box, Button, Grid2, Stack } from '@mui/material';
import { useEffect, useState } from 'react';
// Components, Layouts, Pages
import ChildrenPage from '~/pages/common/childrenPage/ChildrenPage';
import { BaseDatePicker, BaseInput, BaseSelect, Loading } from '~/components/common';
// Others
import { adminRouteAbsolute } from '~/utils/routes/admin';
import { IBaseOption, IQueryParamBase } from '~/utils/interface/common';
import { IFormTask } from '~/utils/interface/task';
import {
  BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
  DEFAULT_DEBOUNCE_DELAY_TIME,
  DEFAULT_LIMIT_1000,
  EMPTY_STRING,
} from '~/utils/constants/common';
import { StorageEnum } from '~/utils/enum/base';
import { RoleEnum, StatusEnum } from '~/utils/enum/common';
import { staffRouteAbsolute } from '~/utils/routes/staff';
import { defaultValuesForm, schema } from './helper';
import { compareDataUpdate, getFullName, removeEmptyObjects } from '~/utils/helper/common';
import useDebounce from '~/utils/hooks/useDebounce';
import { useAppDispatch } from '~/utils/redux/hooks';
import { getEmployeeList } from '~/thunks/employee/employeeThunk';
import { createTask, getTaskDetail, updateTask } from '~/thunks/task/taskThunk';
// Styles, images, icons
import styles from './FormTask.module.scss';

type Props = {};

const cx = classNames.bind(styles);

const FormTask = (props: Props) => {
  //#region Destructuring Props

  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['admin/task', 'common/base']);
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<IFormTask>({
    resolver: yupResolver(schema(t)),
    defaultValues: defaultValuesForm,
  });
  const currentRole: string = localStorage.getItem(StorageEnum.ROLE) ?? EMPTY_STRING;
  const { id: taskId } = useParams();
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [employeeList, setEmployeeList] = useState<IBaseOption[]>([]);
  const [isLoadingAssignee, setIsLoadingAssignee] = useState<boolean>(false);
  const [assigneeSearchKey, setAssigneeSearchKey] = useState<string>(EMPTY_STRING);
  const debounceAssigneeSearchKey = useDebounce(assigneeSearchKey, DEFAULT_DEBOUNCE_DELAY_TIME);

  const [initialData, setInitialData] = useState<IFormTask>(defaultValuesForm);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  useEffect(() => {
    const params: IQueryParamBase = {
      page: BASE_PAGINATION_DEFAULT_CURRENT_PAGE,
      limit: DEFAULT_LIMIT_1000,
      status: StatusEnum.ACTIVE,
      ...(debounceAssigneeSearchKey && { textSearch: debounceAssigneeSearchKey }),
    };

    handleGetEmployeeList(params);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debounceAssigneeSearchKey]);

  useEffect(() => {
    if (!taskId) return;

    handleGetTaskDetail(taskId);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskId]);
  //#endregion Implement Hook

  //#region Handle Function
  const handleGetTaskDetail = (taskId: string) => {
    if (!taskId) return;

    setIsLoading(true);

    dispatch(getTaskDetail(taskId))
      .unwrap()
      .then((res) => {
        const taskDetail = res?.data;

        const newData: IFormTask = {
          title: taskDetail?.title || EMPTY_STRING,
          accountId: taskDetail?.assignee?.id || EMPTY_STRING,
          dueDate: taskDetail?.dueDate || EMPTY_STRING,
          description: taskDetail?.description || EMPTY_STRING,
        };

        setInitialData(newData);
        reset(newData);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleGetEmployeeList = (params: IQueryParamBase) => {
    setIsLoadingAssignee(true);
    dispatch(getEmployeeList(params))
      .unwrap()
      .then((res) => {
        const { responses } = res?.data;
        if (!responses) return;

        const dataEmployee: IBaseOption[] = responses.map((item) => ({
          value: item.id,
          label: getFullName({ firstName: item.firstName, lastName: item.lastName }),
        }));

        setEmployeeList(dataEmployee);
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoadingAssignee(false);
      });
  };

  const handleCancelFormTask = () => {
    switch (currentRole) {
      case RoleEnum.ADMIN:
        navigate(adminRouteAbsolute.tasks);
        break;

      case RoleEnum.STAFF:
        navigate(staffRouteAbsolute.tasks);
        break;
    }

    reset(defaultValuesForm);
  };

  const handleSubmitFormTask = (data: IFormTask) => {
    if (taskId) {
      handleEditTask(data);
    } else {
      handleCreateTask(data);
    }
  };

  const handleCreateTask = (data: IFormTask) => {
    const formattedData: IFormTask = removeEmptyObjects(data) as IFormTask;

    setIsLoading(true);

    dispatch(createTask(formattedData))
      .unwrap()
      .then((res) => {
        handleCancelFormTask();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleEditTask = (data: IFormTask) => {
    const dataChange: IFormTask = compareDataUpdate(initialData, data) as IFormTask;

    if (!taskId || !dataChange) return;

    setIsLoading(true);

    dispatch(updateTask({ taskId, body: dataChange }))
      .unwrap()
      .then((res) => {
        handleCancelFormTask();
      })
      .catch((error) => {})
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleSearchAssigneeChange = (searchKey: string) => {
    setAssigneeSearchKey(searchKey);
  };
  //#endregion Handle Function

  return (
    <ChildrenPage
      id='formTaskPage'
      title={t(taskId ? 'form_task_edit_title' : 'form_task_add_title')}
      onBackToPreviousPage={handleCancelFormTask}
    >
      <Stack
        component={'form'}
        spacing={'20px'}
        onSubmit={handleSubmit(handleSubmitFormTask)}
        className={cx('form')}
      >
        <Box component={'div'} className={cx('formBody')}>
          <Grid2 container rowSpacing={'24px'} columnSpacing={'24px'}>
            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='title'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseInput
                      required
                      label={t('form_task_label_name')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.title?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 container size={12} rowSpacing={'12px'} columnSpacing={'12px'}>
              <Grid2 size={6}>
                <Controller
                  name='accountId'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseSelect
                      label={t('form_task_label_assignee')}
                      value={value}
                      options={employeeList || []}
                      onChange={({ value: optionValue }: IBaseOption) => onChange(optionValue)}
                      onSearchChange={handleSearchAssigneeChange}
                      isLoading={isLoadingAssignee}
                      errorMessage={errors.accountId?.message}
                    />
                  )}
                />
              </Grid2>

              <Grid2 size={6}>
                <Controller
                  name='dueDate'
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <BaseDatePicker
                      label={t('form_task_label_due_date')}
                      value={value}
                      onChange={onChange}
                      errorMessage={errors?.dueDate?.message}
                    />
                  )}
                />
              </Grid2>
            </Grid2>

            <Grid2 size={12}>
              <Controller
                name='description'
                control={control}
                render={({ field: { value, onChange } }) => (
                  <BaseInput
                    multiline
                    height={'auto'}
                    rows={6}
                    label={t('form_task_label_description')}
                    value={value}
                    onChange={onChange}
                    errorMessage={errors?.description?.message}
                  />
                )}
              />
            </Grid2>
          </Grid2>
        </Box>

        <Stack
          direction={'row'}
          alignItems={'center'}
          justifyContent={'flex-end'}
          component={'div'}
          spacing={'12px'}
          className={cx('formFooter')}
        >
          <Button variant='outlined' onClick={handleCancelFormTask}>
            {t('common_action_cancel', { ns: 'common/base' })}
          </Button>

          <Button variant='contained' type='submit' disabled={taskId ? !isDirty : false}>
            {t('common_action_save', { ns: 'common/base' })}
          </Button>
        </Stack>

        {isLoading && <Loading />}
      </Stack>
    </ChildrenPage>
  );
};

export default FormTask;
